import type { GenerateGccPayload, GccJobState, GccTaxonomyRead, RunTnOnRetainedPayload, CombineGccTnPayload } from '@shared/types'

type PostJsonFn = <T>(path: string, body: unknown) => Promise<T>
type DeleteJsonFn = <T>(path: string) => Promise<T>

export async function generateGcc(
  postJson: PostJsonFn,
  payload: GenerateGccPayload,
): Promise<{ jobId: string; error?: string }> {
  const result = await postJson<{ jobId: string }>('/ai/gcc', payload)
  return { jobId: result.jobId }
}

/** WI 2888: how much of the taxonomy text Step 0 would read, for the Step 1 size warning. */
export async function checkGccTaxonomy(
  postJson: PostJsonFn,
  files: Array<{ base64: string; name: string }>,
): Promise<GccTaxonomyRead> {
  return postJson<GccTaxonomyRead>('/ai/gcc/taxonomy-check', { files })
}

export async function cancelGccJob(base: string, jobId: string): Promise<void> {
  try {
    await fetch(`${base}/ai/gcc/jobs/${encodeURIComponent(jobId)}/cancel`, {
      method: 'POST',
      credentials: 'include',
    })
  } catch { /* best-effort */ }
}

export async function getGccJob(base: string, jobId: string): Promise<GccJobState | null> {
  const res = await fetch(`${base}/ai/gcc/jobs/${encodeURIComponent(jobId)}`, {
    credentials: 'include',
  })
  if (res.status === 404) return null
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
  return (await res.json()) as GccJobState
}

export async function runTnOnRetainedWorkforce(
  postJson: PostJsonFn,
  payload: RunTnOnRetainedPayload,
): Promise<{ jobId: string; projectId: string }> {
  return postJson<{ jobId: string; projectId: string }>('/ai/run-tn-on-retained', payload)
}

export async function combineGccTnAnalyses(
  postJson: PostJsonFn,
  payload: CombineGccTnPayload,
): Promise<{ projectId: string }> {
  return postJson<{ projectId: string }>('/ai/gcc/combine', payload)
}

export async function seedGccDemo(postJson: PostJsonFn): Promise<{ seeded: number }> {
  return postJson<{ seeded: number }>('/ai/gcc/demo/seed', {})
}

export async function resetGccDemo(deleteJson: DeleteJsonFn): Promise<{ deleted: number }> {
  return deleteJson<{ deleted: number }>('/ai/gcc/demo/reset')
}
